import { GRACKLE_COLORS } from '@/lib/theme'
import { useOverflowFocus } from '@/hooks/use-overflow-focus'
import type { BillingsMatrix as Matrix, BillingsMatrixRow } from '@/types'

interface BillingsMatrixTableProps {
  matrix: Matrix
  /** Column header for the row-label column, e.g. "Rep" or "Product". */
  dimensionLabel: string
  caption: string
  /** Renders the row label as a link when a destination is returned. */
  linkFor?: (row: BillingsMatrixRow) => string | null
  /** Extra note under a row label, e.g. the House portion of a rep's total. */
  noteFor?: (row: BillingsMatrixRow) => string | null
  /** Render values as percentages rather than currency. */
  asPercent?: boolean
  /**
   * What the rightmost column totals. Defaults to the fiscal year because that is what
   * every caller showed before a window could be narrower than one.
   */
  totalLabel?: string
  emptyMessage?: string
}

/**
 * A month x dimension grid, shared by every Sales Trackers page.
 *
 * One component rather than six bespoke tables, following the training worksheet
 * pattern: the pages differ only in which numbers they pass in.
 *
 * Accessibility carries most of the weight here, because a 14-column numeric table
 * is where it usually gets dropped:
 *  - a real <caption>, and scope on every header, so a screen reader can announce
 *    "Tom Nugent, Jun 2025, 40,537" instead of reading a wall of numbers
 *  - the grid scrolls inside its own container, so the page never scrolls sideways
 *  - tabular-nums so figures line up in a column
 *  - negatives are marked in TEXT with a minus sign as well as colour, since a
 *    credit note is exactly the row a colour-blind reader must not misread
 */
export function BillingsMatrixTable({
  matrix,
  dimensionLabel,
  caption,
  linkFor,
  noteFor,
  asPercent = false,
  totalLabel = 'FY total',
  emptyMessage = 'No billings for this period.',
}: BillingsMatrixTableProps) {
  const { months, rows, totals } = matrix

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground py-6">{emptyMessage}</p>
  }

  const fmt = (n: number) => {
    if (asPercent) return `${n.toFixed(1)}%`
    // Whole dollars: these are annual figures and the cents are noise on screen.
    // The CSV export carries the exact values.
    return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
  }

  // Colour is a secondary cue only; the minus sign is the primary one.
  const cellClass = (n: number) =>
    n < 0 ? 'text-destructive' : n === 0 ? 'text-muted-foreground' : undefined

  const overflow = useOverflowFocus(caption)

  return (
    <div className="overflow-x-auto" ref={overflow.ref} {...overflow.regionProps}>
      <table
        className="w-full text-sm border-collapse"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b">
            <th scope="col" className="text-left font-medium py-2 pr-4 sticky left-0 bg-background">
              {dimensionLabel}
            </th>
            {months.map((m) => (
              <th
                key={m.key}
                scope="col"
                className="text-right font-medium py-2 px-2 whitespace-nowrap"
              >
                {m.label}
              </th>
            ))}
            <th scope="col" className="text-right font-semibold py-2 pl-3 whitespace-nowrap">
              {/* Not "FY total": this column totals whatever window the server returned,
                  which is one month when somebody filtered to one. */}
              {months.length === 1 ? 'Total' : totalLabel}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const href = linkFor?.(row) ?? null
            const note = noteFor?.(row) ?? null
            return (
              <tr key={row.label} className="border-b last:border-0 hover:bg-muted/40">
                {/* row header, so a screen reader announces the rep with each cell */}
                <th
                  scope="row"
                  className="text-left font-normal py-1.5 pr-4 sticky left-0 bg-background"
                >
                  {href ? (
                    <a href={href} className="underline">
                      {row.label}
                    </a>
                  ) : (
                    row.label
                  )}
                  {note && <span className="block text-xs text-muted-foreground">{note}</span>}
                </th>
                {months.map((m) => {
                  const v = row.months[m.key] ?? 0
                  return (
                    <td key={m.key} className={`text-right py-1.5 px-2 ${cellClass(v) ?? ''}`}>
                      {fmt(v)}
                    </td>
                  )
                })}
                <td
                  className={`text-right py-1.5 pl-3 font-semibold ${cellClass(row.total) ?? ''}`}
                >
                  {fmt(row.total)}
                </td>
              </tr>
            )
          })}
        </tbody>
        {!asPercent && (
          <tfoot>
            <tr className="border-t-2" style={{ borderColor: GRACKLE_COLORS.prussianBlue }}>
              <th
                scope="row"
                className="text-left py-2 pr-4 font-semibold sticky left-0 bg-background"
              >
                Total
              </th>
              {months.map((m) => (
                <td key={m.key} className="text-right py-2 px-2 font-semibold">
                  {fmt(totals.months[m.key] ?? 0)}
                </td>
              ))}
              <td className="text-right py-2 pl-3 font-semibold">{fmt(totals.total)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}
